import type { InputT } from "@/types/database/forms";
import type { NextEntryT } from "@/types/database/trackers";
import type { AnyRoute } from "@tanstack/react-router";
import { useNavigate, useParams } from "@tanstack/react-router";
import useFormPage from "./useFormPage";

import { Route as TrackerCompleteRoute } from "@/routes/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/complete";
import { Route as TrackerPageIndexRoute } from "@/routes/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/page/$pageIndex";

interface Props {
	route: AnyRoute;
	pageCount: number;
	inputs: InputT[];
	nextEntry: NextEntryT;
}

const useTrackerFormPage = (props: Props) => {
	const navigate = useNavigate();
	const params = useParams({ from: props.route?.id });
	const diaryId = (params as any)?.diaryId ?? "";
	const trackerId = (params as any)?.trackerId ?? "";
	const entryId = (params as any)?.entryId ?? "";
	const pageIndex = Number((params as any)?.pageIndex ?? 0);

	const nextPageFn = (newPageIndex: number) => {
		navigate({
			to: TrackerPageIndexRoute.to,
			params: (prev: any) => ({ ...prev, pageIndex: String(newPageIndex) }),
		});
	};

	const prevPageFn = (newPageIndex: number) => {
		navigate({
			to: TrackerPageIndexRoute.to,
			params: (prev: any) => ({ ...prev, pageIndex: String(newPageIndex) }),
		});
	};

	// Simplified successFn - we already have trackerId from params
	const successFn = () => {
		if (trackerId) {
			navigate({
				to: TrackerPageIndexRoute.to,
				params: {
					diaryId,
					trackerId,
					entryId,
					pageIndex: String(pageIndex),
				},
			});
		}
	};

	// Simplified completeFn - we already have trackerId from params
	const completeFn = () => {
		if (diaryId && trackerId && entryId) {
			navigate({
				to: TrackerCompleteRoute.to,
				params: {
					diaryId,
					trackerId,
					entryId,
				},
			});
		}
	};

	// Create a custom hook props object that matches the expected interface
	const formPageProps = {
		route: props.route,
		inputs: props.inputs,
		pageCount: props.pageCount,
		nextEntry: props.nextEntry,
		formType: "tracker" as const,
		nextPageFn,
		prevPageFn,
		successFn,
		completeFn,
	};

	const { form, onSubmit, prevPage } = useFormPage(formPageProps);

	// Create a nextPage function that calls nextPageFn with the next page index
	const nextPage = () => {
		nextPageFn(pageIndex + 1);
	};

	return {
		form,
		onSubmit,
		prevPage,
		nextPage,
	};
};

export default useTrackerFormPage;
